import { createContext, useContext, useEffect, useState } from 'react';
import { useApiCall } from 'use-api-call';
import clientSvc from '../../../../services/client/clientSvc';
import type { RawData, PayloadData, PaymentsContextType } from '../types';

interface IFormProvider {
  children: JSX.Element;
  initialValues?: RawData;
}

export const FormContext = createContext<PaymentsContextType>(
  {} as PaymentsContextType
);

export const PaymentFormProvider = ({
  children,
  initialValues = {} as RawData,
}: IFormProvider) => {
  const [data, setData] = useState(initialValues);
  const [apiResponse, setApiResponse] = useState(undefined);
  const [apiError, setApiError] = useState<Error | undefined>(undefined);
  const [apiLoading, setApiLoading] = useState<boolean>(false);

  const setFormValues = (values: any) => {
    setData((prevValues) => {
      const newData = {
        ...prevValues,
        ...values,
      };

      if (
        (!newData.resultAmount ||
          newData.resultAmount === '0' ||
          newData.resultAmount === 0) &&
        prevValues.resultAmount &&
        String(prevValues.resultAmount) !== '0' &&
        Number(prevValues.resultAmount) !== 0
      ) {
        newData.resultAmount = prevValues.resultAmount;
      }

      return newData;
    });
  };

  const {
    data: responseData,
    error: responseError,
    invoke: generateTransfer,
    loading: responseLoading,
  } = useApiCall((contactData: PayloadData) =>
    clientSvc.generateTransfer(contactData)
  );

  const {
    data: responseSwiftData,
    error: responseSwiftError,
    invoke: generateSwiftTransfer,
    loading: responseSwiftLoading,
  } = useApiCall((contactData: PayloadData, enterpriseId: string) =>
    clientSvc.generateSwiftTransfer(contactData, enterpriseId)
  );

  const clearFormValues = () => {
    setData(initialValues);
    setApiResponse(undefined);
  };

  useEffect(() => {
    if (responseData) {
      setApiResponse(responseData);
    }
    if (responseSwiftData) {
      setApiResponse(responseSwiftData);
    }
  }, [responseData, responseSwiftData]);

  useEffect(() => {
    setApiError(responseError || responseSwiftError);
  }, [responseError, responseSwiftError]);

  useEffect(() => {
    setApiLoading(responseLoading || responseSwiftLoading);
  }, [responseLoading, responseSwiftLoading]);

  return (
    <FormContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        data,
        responseData: apiResponse,
        responseError: apiError,
        responseLoading: apiLoading,
        generateTransfer,
        generateSwiftTransfer,
        setFormValues,
        clearFormValues,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormData = () => useContext(FormContext);
